import React from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Illustration } from '../common/Illustration';

export function LoadingState({ label = 'Loading CUET resources…' }: { label?: string }) {
  return (
    <div className="state-view" role="status" aria-live="polite">
      <div className="orbital-loader" aria-hidden="true">
        <span className="orbit-ring ring-outer" />
        <span className="orbit-ring ring-inner" />
        <Loader2 className="state-spinner" size={22} />
      </div>
      <p>{label}</p>
    </div>
  );
}

export function ErrorState({
  message = 'Something went wrong while fetching data.',
  onRetry,
  illustrationContext = 'error.network',
}: {
  message?: string;
  onRetry?: () => void;
  illustrationContext?: string;
}) {
  return (
    <div className="state-view" role="alert">
      <Illustration context={illustrationContext} size="lg" className="state-illustration" />
      <AlertCircle className="state-icon is-error visually-hidden" size={30} aria-hidden="true" />
      <p>{message}</p>
      {onRetry && (
        <button type="button" className="wfx-btn wfx-btn-secondary" style={{ marginTop: '12px' }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Skeleton({ width, height = 14, radius = 8 }: { width?: string | number; height?: number; radius?: number }) {
  return <span className="skeleton" style={{ width: width ?? '100%', height, borderRadius: radius }} aria-hidden="true" />;
}

export function SkeletonCards({ count = 4 }: { count?: number }) {
  return (
    <div className="skeleton-cards" role="status" aria-label="Loading" aria-live="polite">
      {Array.from({ length: count }).map((_, i) => (
        <div className="skeleton-card glass-surface" key={i} style={{ padding: '20px', borderRadius: '16px' }}>
          <Skeleton width="40%" height={14} />
          <Skeleton width="80%" height={24} />
          <Skeleton width="100%" height={12} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-list" role="status" aria-label="Loading" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} height={44} radius={12} />)}
    </div>
  );
}

export function EmptyState({
  title = 'No CUET items found',
  message,
  illustrationContext = 'empty.default',
  action,
}: {
  title?: string;
  message?: string;
  illustrationContext?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="state-view">
      <Illustration context={illustrationContext} size="lg" className="state-illustration" />
      <strong style={{ fontSize: '1.15rem', marginTop: '16px', display: 'block' }}>{title}</strong>
      {message && <p className="wfx-muted" style={{ marginTop: '6px', maxWidth: '44ch' }}>{message}</p>}
      {action && <div style={{ marginTop: '16px' }}>{action}</div>}
    </div>
  );
}
